package com.fantasytenis.web.screens.survivor

import androidx.compose.runtime.*
import com.fantasytenis.shared.core.utils.SharedData
import com.fantasytenis.shared.domain.survivor.entity.SurvivorContestEntity
import com.fantasytenis.shared.domain.survivor.entity.SurvivorContestStatus
import com.fantasytenis.shared.domain.survivor.entity.SurvivorVariation
import com.fantasytenis.web.components.Button
import com.fantasytenis.web.components.ResponsiveHeader
import com.fantasytenis.web.di.DependencyContainer
import com.fantasytenis.web.navigation.NavigationState
import com.fantasytenis.web.navigation.Route
import com.fantasytenis.web.theme.Colors
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/**
 * Screen displaying list of available survivor contests.
 */
@Composable
fun SurvivorContestListScreen() {
    val scope = rememberCoroutineScope()
    val showPaidGames by SharedData.showPaidGames.collectAsState()
    var contests by remember { mutableStateOf<List<SurvivorContestEntity>>(emptyList()) }
    var isLoading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }
    var selectedFilter by remember { mutableStateOf<SurvivorContestStatus?>(null) }

    // Load contests on mount or filter change
    LaunchedEffect(selectedFilter, showPaidGames) {
        isLoading = true
        error = null
        val statusValue = selectedFilter?.let { status ->
            when (status) {
                SurvivorContestStatus.UPCOMING -> 1
                SurvivorContestStatus.ACTIVE -> 2
                SurvivorContestStatus.COMPLETED -> 3
                SurvivorContestStatus.CANCELLED -> 4
                SurvivorContestStatus.PAYING_OUT -> 5
            }
        }

        DependencyContainer.listSurvivorContestsUseCase(
            status = statusValue,
            featuredOnly = false,
            limit = 50
        ).fold(
            onSuccess = { list ->
                // Filter based on platform setting
                contests = if (showPaidGames) list else list.filter { it.entryFee == 0 }
                isLoading = false
            },
            onFailure = { e ->
                error = e.message
                isLoading = false
            }
        )
    }

    Div({
        style {
            property("height", "var(--app-height)")
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            backgroundColor(Colors.Background)
        }
    }) {
        // Header
        ResponsiveHeader(
            title = "Survivor Contests",
            showBackButton = true,
            onBackClick = { NavigationState.navigate(Route.Home) }
        )

        // Main Content - Scrollable area
        Main({
            style {
                flex(1)
                overflowY("auto")
                property("-webkit-overflow-scrolling", "touch")
            }
        }) {
            // Inner container for max-width and padding
            Div({
                style {
                    padding(24.px)
                    maxWidth(1200.px)
                    margin(0.px, "auto".unsafeCast<CSSNumeric>())
                }
            }) {
            // How it works section
            HowItWorks()

            // Filter Tabs
            FilterTabs(
                selected = selectedFilter,
                onSelect = { selectedFilter = it }
            )

            // Content
            when {
                isLoading -> LoadingState()
                error != null -> ErrorState(error!!) {
                    scope.launch {
                        isLoading = true
                        DependencyContainer.listSurvivorContestsUseCase().fold(
                            onSuccess = { contests = it; isLoading = false },
                            onFailure = { error = it.message; isLoading = false }
                        )
                    }
                }
                contests.isEmpty() -> EmptyState()
                else -> ContestGrid(contests)
            }
            }
        }
    }
}

@Composable
private fun HowItWorks() {
    Div({
        style {
            backgroundColor(Colors.Surface)
            borderRadius(12.px)
            padding(20.px)
            marginBottom(24.px)
            property("box-shadow", "0 2px 8px rgba(0, 0, 0, 0.05)")
        }
    }) {
        H3({
            style {
                fontSize(16.px)
                fontWeight(600)
                color(Colors.TextPrimary)
                marginBottom(12.px)
                margin(0.px)
            }
        }) {
            Text("How Survivor Contests Work")
        }

        Div({
            style {
                display(DisplayStyle.Grid)
                property("grid-template-columns", "repeat(auto-fit, minmax(200px, 1fr))")
                gap(16.px)
                marginTop(12.px)
            }
        }) {
            HowItWorksStep("1", "Pick a Player", "Choose one player per round to win their match")
            HowItWorksStep("2", "Survive", "If your player wins, you advance to the next round")
            HowItWorksStep("3", "No Repeats", "Each player can only be used once per contest")
            HowItWorksStep("4", "Last Standing", "Be the last survivor to win the prize pool!")
        }
    }
}

@Composable
private fun HowItWorksStep(number: String, title: String, description: String) {
    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.FlexStart)
            gap(12.px)
        }
    }) {
        // Number badge
        Div({
            style {
                width(28.px)
                height(28.px)
                borderRadius(50.percent)
                backgroundColor(Colors.Primary)
                color(Colors.White)
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.Center)
                alignItems(AlignItems.Center)
                fontSize(14.px)
                fontWeight(600)
                flexShrink(0)
            }
        }) {
            Text(number)
        }

        Div {
            Div({
                style {
                    fontWeight(600)
                    fontSize(14.px)
                    color(Colors.TextPrimary)
                    marginBottom(2.px)
                }
            }) {
                Text(title)
            }
            Div({
                style {
                    fontSize(13.px)
                    color(Colors.TextSecondary)
                }
            }) {
                Text(description)
            }
        }
    }
}

@Composable
private fun FilterTabs(
    selected: SurvivorContestStatus?,
    onSelect: (SurvivorContestStatus?) -> Unit
) {
    Div({
        style {
            display(DisplayStyle.Flex)
            gap(8.px)
            marginBottom(24.px)
            flexWrap(FlexWrap.Wrap)
        }
    }) {
        FilterTab("All", selected == null) { onSelect(null) }
        FilterTab("Upcoming", selected == SurvivorContestStatus.UPCOMING) {
            onSelect(SurvivorContestStatus.UPCOMING)
        }
        FilterTab("Active", selected == SurvivorContestStatus.ACTIVE) {
            onSelect(SurvivorContestStatus.ACTIVE)
        }
        FilterTab("Completed", selected == SurvivorContestStatus.COMPLETED) {
            onSelect(SurvivorContestStatus.COMPLETED)
        }
    }
}

@Composable
private fun FilterTab(
    label: String,
    isSelected: Boolean,
    onClick: () -> Unit
) {
    Div({
        style {
            padding(8.px, 16.px)
            borderRadius(20.px)
            cursor("pointer")
            fontSize(14.px)
            fontWeight(if (isSelected) 600 else 400)
            backgroundColor(if (isSelected) Colors.Primary else Colors.Surface)
            color(if (isSelected) Colors.White else Colors.TextPrimary)
            property("transition", "all 0.2s")
        }
        onClick { onClick() }
    }) {
        Text(label)
    }
}

@Composable
private fun LoadingState() {
    Div({
        style {
            display(DisplayStyle.Flex)
            justifyContent(JustifyContent.Center)
            alignItems(AlignItems.Center)
            padding(48.px)
        }
    }) {
        Div({
            style {
                width(40.px)
                height(40.px)
                border(4.px, LineStyle.Solid, Colors.Primary)
                borderRadius(50.percent)
                property("border-top-color", "transparent")
                property("animation", "spin 1s linear infinite")
            }
        })
    }
}

@Composable
private fun ErrorState(message: String, onRetry: () -> Unit) {
    Div({
        style {
            textAlign("center")
            padding(48.px)
        }
    }) {
        P({
            style {
                color(Colors.Error)
                fontSize(16.px)
                marginBottom(16.px)
            }
        }) {
            Text("Error: $message")
        }
        Button(
            text = "Retry",
            onClick = onRetry,
            variant = "primary"
        )
    }
}

@Composable
private fun EmptyState() {
    Div({
        style {
            textAlign("center")
            padding(48.px)
        }
    }) {
        P({
            style {
                color(Colors.TextSecondary)
                fontSize(16.px)
            }
        }) {
            Text("No survivor contests available")
        }
    }
}

@Composable
private fun ContestGrid(contests: List<SurvivorContestEntity>) {
    Div({
        style {
            display(DisplayStyle.Grid)
            property("grid-template-columns", "repeat(auto-fill, minmax(min(100%, 320px), 1fr))")
            gap(24.px)
        }
    }) {
        contests.forEach { contest ->
            ContestCard(contest)
        }
    }
}

@Composable
private fun ContestCard(contest: SurvivorContestEntity) {
    Div({
        classes("baseline-card")
        style {
            backgroundColor(Colors.Surface)
            borderRadius(12.px)
            padding(20.px)
            property("box-shadow", "0 2px 8px rgba(0, 0, 0, 0.1)")
            cursor("pointer")
            property("transition", "transform 0.2s, box-shadow 0.2s")
        }
        onClick {
            NavigationState.navigate(Route.SurvivorContestDetail(contest.id))
        }
    }) {
        // Header with badges
        Div({
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                alignItems(AlignItems.FlexStart)
                marginBottom(12.px)
            }
        }) {
            // Left badges
            Div({
                style {
                    display(DisplayStyle.Flex)
                    gap(8.px)
                    flexWrap(FlexWrap.Wrap)
                }
            }) {
                // Featured badge
                if (contest.isFeatured) {
                    Badge("FEATURED", Colors.Secondary, Colors.TextPrimary)
                }
                // Variation badge
                VariationBadge(contest.variation)
            }

            // Status badge
            StatusBadge(contest.status)
        }

        // Contest name
        H3({
            style {
                fontSize(18.px)
                fontWeight(600)
                color(Colors.TextPrimary)
                marginBottom(4.px)
                margin(0.px)
            }
        }) {
            Text(contest.name)
        }

        // Tournament name
        P({
            style {
                color(Colors.TextSecondary)
                fontSize(13.px)
                marginBottom(16.px)
                margin(0.px)
                marginTop(4.px)
            }
        }) {
            Text(contest.tournamentName)
        }

        // Entry info row
        Div({
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                marginBottom(12.px)
            }
        }) {
            InfoItem("Entry Fee", contest.entryFeeDisplay)
            InfoItem("Prize Pool", contest.prizePoolDisplay)
        }

        // Entries and Alive stats
        Div({
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                marginBottom(16.px)
                padding(12.px)
                backgroundColor(Colors.SurfaceVariant)
                borderRadius(8.px)
            }
        }) {
            StatItem("Entries", "${contest.currentEntries}/${contest.maxEntries ?: "∞"}")
            StatItem("Alive", "${contest.aliveEntries}", if (contest.status == SurvivorContestStatus.ACTIVE) Colors.Success else null)
            contest.currentRound?.let { round ->
                StatItem("Round", round.displayName)
            }
        }

        // Strike limit info (if applicable)
        if (contest.variation == SurvivorVariation.STRIKE_SYSTEM && contest.strikeLimit > 0) {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(4.px)
                    marginBottom(12.px)
                    fontSize(13.px)
                    color(Colors.TextSecondary)
                }
            }) {
                Text("${contest.strikeLimit} strikes allowed")
            }
        }

        // Rebuy info (if applicable)
        if (contest.variation == SurvivorVariation.REBUY_ALLOWED && contest.rebuyFee > 0) {
            Div({
                style {
                    display(DisplayStyle.Flex)
                    alignItems(AlignItems.Center)
                    gap(4.px)
                    marginBottom(12.px)
                    fontSize(13.px)
                    color(Colors.TextSecondary)
                }
            }) {
                Text("Rebuy available: ${formatCurrency(contest.rebuyFee)}")
            }
        }

        // Action button
        when (contest.status) {
            SurvivorContestStatus.UPCOMING -> {
                if (!contest.isFull) {
                    Button(
                        text = "Enter Contest",
                        onClick = {
                            NavigationState.navigate(Route.SurvivorContestDetail(contest.id))
                        },
                        variant = "primary"
                    )
                } else {
                    Button(
                        text = "Contest Full",
                        onClick = {},
                        variant = "disabled"
                    )
                }
            }
            SurvivorContestStatus.ACTIVE -> {
                Button(
                    text = "View Leaderboard",
                    onClick = {
                        NavigationState.navigate(Route.SurvivorLeaderboard(contest.id))
                    },
                    variant = "secondary"
                )
            }
            else -> {
                Button(
                    text = "View Results",
                    onClick = {
                        NavigationState.navigate(Route.SurvivorContestDetail(contest.id))
                    },
                    variant = "secondary"
                )
            }
        }
    }
}

@Composable
private fun Badge(text: String, bgColor: CSSColorValue, textColor: CSSColorValue) {
    Span({
        style {
            backgroundColor(bgColor)
            color(textColor)
            fontSize(11.px)
            fontWeight(600)
            padding(4.px, 8.px)
            borderRadius(4.px)
        }
    }) {
        Text(text)
    }
}

@Composable
private fun VariationBadge(variation: SurvivorVariation) {
    val (bgColor, textColor, label) = when (variation) {
        SurvivorVariation.STRAIGHT_KNOCKOUT -> Triple(rgb(255, 235, 238), rgb(211, 47, 47), "Knockout")
        SurvivorVariation.STRIKE_SYSTEM -> Triple(rgb(255, 243, 224), rgb(245, 124, 0), "Strikes")
        SurvivorVariation.REBUY_ALLOWED -> Triple(rgb(232, 245, 233), rgb(56, 142, 60), "Rebuy")
    }

    Span({
        style {
            backgroundColor(bgColor)
            color(textColor)
            fontSize(11.px)
            fontWeight(600)
            padding(4.px, 8.px)
            borderRadius(4.px)
        }
    }) {
        Text(label)
    }
}

@Composable
private fun StatusBadge(status: SurvivorContestStatus) {
    val (bgColor, textColor, label) = when (status) {
        SurvivorContestStatus.UPCOMING -> Triple(rgb(227, 242, 253), rgb(25, 118, 210), "Upcoming")
        SurvivorContestStatus.ACTIVE -> Triple(rgb(232, 245, 233), rgb(56, 142, 60), "Active")
        SurvivorContestStatus.COMPLETED -> Triple(rgb(245, 245, 245), rgb(117, 117, 117), "Completed")
        SurvivorContestStatus.CANCELLED -> Triple(rgb(255, 235, 238), rgb(211, 47, 47), "Cancelled")
        SurvivorContestStatus.PAYING_OUT -> Triple(rgb(255, 243, 224), rgb(245, 124, 0), "Paying Out")
    }

    Span({
        style {
            backgroundColor(bgColor)
            color(textColor)
            fontSize(11.px)
            fontWeight(600)
            padding(4.px, 8.px)
            borderRadius(4.px)
        }
    }) {
        Text(label)
    }
}

@Composable
private fun InfoItem(label: String, value: String) {
    Div {
        Div({
            style {
                fontSize(11.px)
                color(Colors.TextSecondary)
                marginBottom(2.px)
            }
        }) {
            Text(label)
        }
        Div({
            style {
                fontSize(16.px)
                fontWeight(600)
                color(Colors.TextPrimary)
            }
        }) {
            Text(value)
        }
    }
}

@Composable
private fun StatItem(label: String, value: String, valueColor: CSSColorValue? = null) {
    Div({
        style {
            textAlign("center")
        }
    }) {
        Div({
            style {
                fontSize(11.px)
                color(Colors.TextSecondary)
                marginBottom(2.px)
            }
        }) {
            Text(label)
        }
        Div({
            style {
                fontSize(14.px)
                fontWeight(600)
                color(valueColor ?: Colors.TextPrimary)
            }
        }) {
            Text(value)
        }
    }
}

private fun formatCurrency(cents: Int): String {
    val dollars = cents / 100.0
    return if (dollars == dollars.toLong().toDouble()) {
        "$${dollars.toLong()}"
    } else {
        val formatted = ((dollars * 100).toLong() / 100.0).toString()
        val decimalIndex = formatted.indexOf('.')
        val result = if (decimalIndex == -1) {
            "$formatted.00"
        } else {
            val decimals = formatted.substring(decimalIndex + 1)
            if (decimals.length == 1) "$formatted${"0"}" else formatted
        }
        "$$result"
    }
}
